import { MetaApiConfig } from '../types/FireAgent';
import { MessageStatistics, DailyUsage, ConsumptionReport } from '../types/FireAgent';

class MetaConsumptionService {
  private config: MetaApiConfig | null = null;

  setConfig(config: MetaApiConfig) {
    this.config = config;
  }

  private getBaseUrl(): string {
    if (!this.config) {
      throw new Error('Configuração do Meta API não encontrada');
    }
    
    const version = this.config.version || 'v24.0';
    return `https://graph.facebook.com/${version}`;
  }

  private getHeaders(): HeadersInit {
    if (!this.config) {
      throw new Error('Configuração do Meta API não encontrada');
    }
    return {
      'Authorization': `Bearer ${this.config.accessToken}`,
      'Content-Type': 'application/json'
    };
  }

  async getMessageStatistics(startDate: string, endDate: string): Promise<MessageStatistics> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.phoneNumberId) {
        throw new Error('Phone Number ID é obrigatório para buscar estatísticas.');
      }

      // Endpoint de analytics da Meta API
      const url = `${this.getBaseUrl()}/${this.config.phoneNumberId}/analytics`;
      
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
        granularity: 'day',
        metric_types: 'sent,delivered,failed'
      });

      const response = await fetch(`${url}?${params.toString()}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `Erro ${response.status}: ${response.statusText}`;
        throw new Error(`Erro ao buscar estatísticas: ${errorMessage}`);
      }

      const data = await response.json();
      
      // Processar dados da API
      const dailyBreakdown: DailyUsage[] = [];
      let totalSent = 0;
      let totalDelivered = 0;
      let totalFailed = 0;

      if (data.data && Array.isArray(data.data)) {
        data.data.forEach((item: any) => {
          const sent = item.sent || 0;
          const delivered = item.delivered || 0;
          const failed = item.failed || 0;
          
          totalSent += sent;
          totalDelivered += delivered;
          totalFailed += failed;

          dailyBreakdown.push({
            date: item.start_time || item.date || '',
            sent,
            delivered,
            failed
          });
        });
      }

      return {
        total_sent: totalSent,
        total_delivered: totalDelivered,
        total_failed: totalFailed,
        period: {
          start: startDate,
          end: endDate
        },
        daily_breakdown: dailyBreakdown
      };
    } catch (error: any) {
      console.error('❌ Erro ao buscar estatísticas:', error);
      throw error;
    }
  }

  async getDailyUsage(startDate: string, endDate: string): Promise<DailyUsage[]> {
    try {
      const statistics = await this.getMessageStatistics(startDate, endDate);
      return statistics.daily_breakdown || [];
    } catch (error: any) {
      console.error('❌ Erro ao buscar uso diário:', error);
      throw error;
    }
  }

  async getWeeklyUsage(startDate: string, endDate: string): Promise<DailyUsage[]> {
    try {
      const dailyUsage = await this.getDailyUsage(startDate, endDate);
      
      // Agrupar por semana
      const weeklyMap = new Map<string, DailyUsage>();
      
      dailyUsage.forEach(day => {
        const date = new Date(day.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay()); // Domingo da semana
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (weeklyMap.has(weekKey)) {
          const week = weeklyMap.get(weekKey)!;
          week.sent += day.sent;
          week.delivered += day.delivered;
          week.failed += day.failed;
        } else {
          weeklyMap.set(weekKey, {
            date: weekKey,
            sent: day.sent,
            delivered: day.delivered,
            failed: day.failed
          });
        }
      });
      
      return Array.from(weeklyMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    } catch (error: any) {
      console.error('❌ Erro ao buscar uso semanal:', error);
      throw error;
    }
  }

  async getMonthlyUsage(year: number, month: number): Promise<ConsumptionReport> {
    try {
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      const statistics = await this.getMessageStatistics(startDate, endDate);
      
      return {
        period: `${month}/${year}`,
        total_messages: statistics.total_sent,
        successful: statistics.total_delivered,
        failed: statistics.total_failed,
        breakdown: statistics.daily_breakdown || []
      };
    } catch (error: any) {
      console.error('❌ Erro ao buscar uso mensal:', error);
      throw error;
    }
  }

  async getCostEstimate(startDate: string, endDate: string): Promise<number | null> {
    try {
      // A API do Meta não fornece custos diretamente
      // Este método pode ser usado para calcular estimativas baseadas em tarifas conhecidas
      // Por enquanto, retorna null pois não temos acesso direto aos custos via API
      return null;
    } catch (error: any) {
      console.error('❌ Erro ao calcular custo estimado:', error);
      return null;
    }
  }
}

export const metaConsumptionService = new MetaConsumptionService();






